import { useEffect, useState } from 'react'

import { useLocation, useParams } from 'react-router-dom'

import { useIntl } from 'react-intl'

import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'

import {
  beePluginState,
  editorState,
  openAIState
} from '../recoil/state/editor'

import { TOAST_VARIANT_WARNING, toastShow } from 'mailup-web-components'

import { fetchSavedRows } from 'Pages/MessageEdit/recoil/actions/beeSavedRowsActions'

import {
  addMergeTagToWhitelistCallback,
  getTotalTokensCountCallback,
  initBeePluginMessageCallback,
  initLoadPluginCallback,
  onBeeSaveCallback,
  saveMessageAsTemplateCallback,
  storeUsedTokensCounterCallback,
  updateAutosaveCallback
} from '../recoil/callbacks/callbacks'

import { checkForMergeTags, useMergeTagsToast } from 'Hooks/mergeTagWarning'

import { modalContentDialogCallback } from '../ModalBeeContentDialog/recoil/callbacks'

import {
  deleteBeeSavedRowCallback,
  editBeeSavedRowCallback
} from 'Pages/MessageEdit/recoil/callbacks/beeSavedRowsCallback'

import styled from '@emotion/styled'
import { useConditions } from 'mailup-react-helpers'

const BeePluginEditor = () => {
  const intl = useIntl()
  const [
    { loadPluginChecked, mergeTags, mergeTagsWhitelist },
    setEditor
  ] = useRecoilState(editorState)

  const { mode, id, template, idTemplate } = useParams()
  const query = new URLSearchParams(useLocation().search)

  const isNewNotificationMessage = query.get('isNew') === 'true'
  const enableNotification = query.get('enableNot') === 'true'

  const [hasDynFld] = useConditions(['uo:DYNFLD'])

  const initLoadPlugin = useRecoilCallback(initLoadPluginCallback, [])
  const initBeePlugin = useRecoilCallback(initBeePluginMessageCallback, [])
  const handleAutosave = useRecoilCallback(updateAutosaveCallback, [])
  const { instance: beePluginInstance } = useRecoilValue(beePluginState)
  const setBeePluginState = useSetRecoilState(beePluginState)

  const saveAsTemplate = useRecoilCallback(saveMessageAsTemplateCallback, [])
  const resetPlugin = useResetRecoilState(beePluginState)
  const resetEditor = useResetRecoilState(editorState)
  const addMergeTagToWhitelist = useRecoilCallback(
    addMergeTagToWhitelistCallback,
    []
  )

  const openModalContentDialog = useRecoilCallback(
    modalContentDialogCallback,
    []
  )

  const handleBeeSave = useRecoilCallback(onBeeSaveCallback, [])

  const editBeeSavedRow = useRecoilCallback(editBeeSavedRowCallback, [])
  const deleteBeeSavedRow = useRecoilCallback(deleteBeeSavedRowCallback, [])
  const storeUsedTokensCounter = useRecoilCallback(
    storeUsedTokensCounterCallback,
    []
  )

  const getTotalTokensCount = useRecoilCallback(getTotalTokensCountCallback, [])

  const { totalTokens, remainingTokens, enableUse, showBuy } = useRecoilValue(
    openAIState
  )

  const handleSaveAsTemplate = json => saveAsTemplate(json)

  const [beeLoadedState, setBeeLoadedState] = useState(false)

  const handleChange = (json, html) => {
    if (hasDynFld) {
      const { valid: mergeTagsFound } = checkForMergeTags(html, {
        ignore: val => val.startsWith('*cnt_')
      })
      mergeTagsToast.use(mergeTagsFound).update()
    }

    setEditor(prev => ({
      ...prev,
      structure: json,
      dirty: true,
      saved: false
    }))
  }

  useEffect(() => {
    initLoadPlugin({
      id: Number(id || 0),
      mode,
      template,
      idTemplate: Number(idTemplate || 0),
      isNewNotificationMessage,
      enableNotification
    })

    return () => {
      resetPlugin()
      resetEditor()
    }
  }, [])

  const mergeTagsToast = useMergeTagsToast({
    ignore: tag => addMergeTagToWhitelist(tag)
  })

  useEffect(() => {
    mergeTagsToast.allowed = [...mergeTags, ...mergeTagsWhitelist]
  }, [mergeTags, mergeTagsWhitelist])

  useEffect(() => {
    const commentId = query.get('cid')
    if (beeLoadedState && commentId) {
      beePluginInstance.showComment(commentId)
    }
    setBeePluginState(prev => ({
      ...prev,
      loaded: beeLoadedState
    }))
  }, [beeLoadedState])

  useEffect(() => {
    const refreshedUsageSettings = {
      addOns: [
        {
          id: 'ai-integration',
          settings: {
            tokensAvailable: showBuy ? totalTokens : undefined,
            tokensUsed: showBuy ? totalTokens - remainingTokens : undefined,
            tokenLabel: showBuy
              ? intl.formatMessage({
                  id: 'Load.Bee.Tokens'
                })
              : '',
            isPromptDisabled: !enableUse
          }
        }
      ]
    }
    if (beePluginInstance) {
      beePluginInstance.loadConfig(refreshedUsageSettings)
    }
  }, [remainingTokens])

  useEffect(() => {
    if (loadPluginChecked) {
      const init = async () => {
        await initBeePlugin({
          settings: {
            onSave: handleBeeSave,
            onSaveAsTemplate: handleSaveAsTemplate,
            onChange: handleChange,
            onAutoSave: handleAutosave,
            onSaveRow: function(rowJSON, rowHTML, pageJSON) {
              // TODO lo si può cancellare?
              //  callback servizio save di savedRow
              //  forse non serve...
              //  rowJSON è una stringa
              //  const o = JSON.parse(rowJSON)
              //  fakeBeeSavedRows.push({ ...o, name: o.metadata.name })
            },
            onComment: function(changeObj, commentsObj, threadUsers) {},
            onInfo: function(data) {
              if (data.code === 1000) {
                const handle = data.detail.handle
                if (handle === 'ai-integration') {
                  storeUsedTokensCounter(data.detail.usage.total_tokens)

                  getTotalTokensCount()
                }
              }
            },
            onLoad: () => {
              setBeeLoadedState(true)
            },
            contentDialog: {
              externalContentURLs: {
                label: intl.formatMessage({
                  id: 'Load.Bee.SearchContents'
                }),
                handler: (resolve, reject) =>
                  openModalContentDialog(resolve, reject)
              },
              onDeleteRow: {
                handler: async (resolve, reject, args) => {
                  //  modale di conferma già gestita dal Bee

                  deleteBeeSavedRow(resolve, reject, args.row)
                }
              },

              onEditRow: {
                handler: async (resolve, reject, args) => {
                  //  args.row.metadata

                  //  args.row
                  //  args.row.metadata

                  //  edit metadati
                  //  save di args merge nuovi metadati
                  //  resolve con
                  //  - true per aggiornare rows (es: confirm delle modifiche)
                  //  - false per non aggiornare rows (es: cancel delle modifiche)

                  editBeeSavedRow(
                    resolve,
                    reject,
                    args.row,
                    args.row.metadata,
                    false,
                    'edit-row'
                  )
                }
              },

              saveRow: {
                handler: function(resolve, reject, args) {
                  //  raccogli metadati
                  //  save di args merge metadati
                  //  resolve con metadati

                  editBeeSavedRow(resolve, reject, args, {}, true, 'new-row')
                }
              }
            },
            hooks: {
              getRows: {
                handler: async (resolve, reject, args) => {
                  try {
                    const rows = await fetchSavedRows(args)

                    resolve(rows)
                  } catch (e) {
                    toastShow(
                      intl.formatMessage({
                        id: 'Load.Bee.SavedRowsNotAvailable'
                      }),
                      TOAST_VARIANT_WARNING,
                      'SavedRowsNotAvailable'
                    )
                    reject(e)
                  }
                }
              }
            }
          }
        })
      }
      init()
    }
  }, [loadPluginChecked])

  return <StyledBeeContainer id='bee-plugin-container' />
}

export default BeePluginEditor

const StyledBeeContainer = styled.div`
  position: absolute;
  top: 60px;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
`
